import { warRank, type WarLegal, type WarView } from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Modal } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { MySeat, SeatView, useNarrow, useSpeech } from './common';

export function WarTable({ scoresOpen, onCloseScores }: { scoresOpen: boolean; onCloseScores: () => void }) {
  const t = useT();
  const view = useGame((s) => s.view) as WarView;
  const legal = useGame((s) => s.legal) as WarLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const acting = useGame((s) => s.acting);
  const speech = useSpeech();
  const narrow = useNarrow();
  const size = narrow ? 'md' : 'lg';
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const myTurn = mySeat !== null && !!turn?.seats.includes(mySeat);
  const me = mySeat ?? 0;
  const them = 1 - me;
  const ended = view.phase === 'MATCH_END';
  const war = view.phase === 'WAR';
  // The cards shown in the middle: this round's flips, or the finished round while the next one has not started.
  const shown = view.table.some((c) => c !== null) ? view.table : (view.last?.cards ?? view.table);
  const mineCard = shown[me] ?? null;
  const theirCard = shown[them] ?? null;
  const lead = mineCard && theirCard ? Math.sign(warRank(mineCard) - warRank(theirCard)) : 0;
  const winner = ended ? (view.counts[0]! === view.counts[1]! ? (view.roundsWon[0]! >= view.roundsWon[1]! ? 0 : 1) : view.counts[0]! > view.counts[1]! ? 0 : 1) : null;
  const ranking = [0, 1].sort((a, b) => (view.counts[b] ?? 0) - (view.counts[a] ?? 0) || (view.roundsWon[b] ?? 0) - (view.roundsWon[a] ?? 0) || a - b);

  let note: string;
  if (ended) note = t('wr.over', { name: name(winner ?? 0) });
  else if (war) note = t('wr.war', { n: view.potSize });
  else if (view.last?.winner !== null && view.last?.winner !== undefined && !view.table.some((c) => c !== null)) {
    note = t('wr.roundWon', { name: name(view.last.winner), n: view.last.won });
  } else note = myTurn ? t('wr.yourFlip') : t('wr.waiting', { name: name(view.turn) });

  const cardAt = (card: string | null, key: string, mine: boolean) => (
    <div key={key} className={`war-slot ${card && lead !== 0 && (lead > 0) === mine && !war ? 'win' : ''}`} style={{ textAlign: 'center' }}>
      {card ? <PlayingCard id={card} size={size} dealIndex={0} /> : <PlayingCard faceDown size={size} />}
    </div>
  );

  return (
    <>
      <div className="stage">
        <div className="felt" />
        {[0, 1].map((s) => (
          <SeatView
            key={s}
            seat={s}
            info={seats[s]}
            mySeat={mySeat}
            total={2}
            turn={turn}
            speech={speech[s]}
            sub={<span className="dim num">{t('wr.cards', { n: view.counts[s] ?? 0 })}</span>}
            tags={(view.roundsWon[s] ?? 0) > 0 ? <span className="tag seen">{t('wr.rounds', { n: view.roundsWon[s]! })}</span> : null}
          />
        ))}
        <div className="felt-center centered">
          <div className="center-note">{t('wr.round', { n: Math.min(view.rounds + 1, view.config.maxRounds), max: view.config.maxRounds })}</div>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', alignItems: 'center' }} aria-label={t('wr.table')}>
            {cardAt(theirCard, 'them', false)}
            {cardAt(mineCard, 'me', true)}
          </div>
          {war && view.laid.some((n) => n > 0) && <span className="pill">{t('wr.laid', { a: view.laid[me] ?? 0, b: view.laid[them] ?? 0 })}</span>}
          {view.raised.some((r) => r !== undefined) && (
            <span className="pill">{view.raised.map((r, s) => (r ? t('wr.raised', { name: name(s) }) : null)).filter(Boolean).join(' · ') || t('wr.stayed')}</span>
          )}
          <div className="card glass" style={{ textAlign: 'center', padding: '6px 12px' }} role="status">
            <strong>{note}</strong>
          </div>
        </div>
        <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={mySeat !== null ? <>{t('wr.cards', { n: view.counts[mySeat] ?? 0 })}</> : null} />
      </div>
      <div className="table-bottom">
        {myTurn && !ended && (
          <div className="action-bar">
            {legal?.flip && (
              <button className="btn primary lg" disabled={acting} onClick={() => void act({ type: 'flip' })}>
                {t('wr.flip')}
              </button>
            )}
            {legal?.stay && (
              <button className="btn primary" disabled={acting} onClick={() => void act({ type: 'stay' })}>
                {t('wr.stay')}
              </button>
            )}
            {legal?.raise && (
              <button className="btn" disabled={acting} onClick={() => void act({ type: 'raise' })}>
                {t('wr.raise')}
              </button>
            )}
          </div>
        )}
      </div>
      <Modal open={scoresOpen} onClose={onCloseScores} title={t('table.scores')}>
        <table className="scoreboard">
          <thead>
            <tr>
              <th>#</th>
              <th>{t('wr.player')}</th>
              <th>{t('wr.cardsCol')}</th>
              <th>{t('wr.roundsCol')}</th>
            </tr>
          </thead>
          <tbody>
            {ranking.map((s, i) => (
              <tr key={s}>
                <td>{i + 1}</td>
                <td className="ellipsis" style={{ maxWidth: 140 }}>{name(s)}</td>
                <td className="num">{view.counts[s] ?? 0}</td>
                <td className="num">{view.roundsWon[s] ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('wr.scoreHint', { max: view.config.maxRounds })}</p>
      </Modal>
    </>
  );
}
